import { formatISO, isSameDay, parseISO, subDays, subYears } from 'date-fns'
import React, { useCallback, useEffect, useReducer, useState } from 'react'
import http from '../http'

export type Order = {
    customer: string,
    documentNumber: string,
    salesOrg: string,
    documentType: string,
    netValue: string,
    currency: string,
    status: string,
    trackingURL: string,
    type: string,
    orderDate: string
}

type ReturnsListContextState = {
    orders: Order[],
    loading: boolean,
    dateFilter: string,
    setDateFilter: React.Dispatch<string>,
    page: number,
    setPage: React.Dispatch<number>,
    reload: () => void,
    hasMaxOrdersReached: boolean
}

export const ReturnsListContext = React.createContext<ReturnsListContextState>({} as ReturnsListContextState)

type DateFilterState = { startDate: string, endDate: string, dateFilter: string }

const DateFilterReducer: React.Reducer<DateFilterState, string> = (_, action) => {
    switch (action) {
        case '30days':
            return { startDate: formatISO(subDays(new Date(), 30)), endDate: formatISO(new Date()), dateFilter: action }
        case '90days':
            return { startDate: formatISO(subDays(new Date(), 90)), endDate: formatISO(new Date()), dateFilter: action }
        case '5years':
            return { startDate: formatISO(subYears(new Date(), 5)), endDate: formatISO(new Date()), dateFilter: action }
        default:
            return { startDate: formatISO(new Date(parseInt(action), 0, 1, 0, 0, 0)), endDate: formatISO(new Date(parseInt(action), 11, 31, 23, 59, 59)), dateFilter: action }
    }
}

const RESTRICTED_SALES_ORGS = ['0002', '0003', '0004', '0007', '0008', '0011', '0012', '0013', '0014', '0015', '0016', '0017', '0018', '0019'];

export const ReturnsListContextProvider: React.FC = ({ children }) => {
    const [orders, setOrders] = useState<Order[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [{ startDate, endDate, dateFilter }, setDateFilter] = useReducer(DateFilterReducer, DateFilterReducer({} as DateFilterState, window.location.pathname === '/' ? '30days' : '5years'))
    const [page, setPage] = useState<number>(0)

    // Check if any order has restricted salesOrg and total orders >= 2 placed today
    const hasMaxOrdersReached = React.useMemo(() => {
        if (!orders || orders.length === 0) return false

        const today = new Date()
        const todaysRestrictedOrders = orders.filter(order => {
            if (!order.orderDate) return false
            const orderDate = parseISO(order.orderDate)
            if (Number.isNaN(orderDate.getTime())) return false
            return isSameDay(orderDate, today) && RESTRICTED_SALES_ORGS.includes(order.salesOrg)
        })

        return todaysRestrictedOrders.length >= 2
    }, [orders])

    const fetch = useCallback(() => {
        setOrders([])
        setLoading(true)
        setPage(0)
        http.get<Order[]>(`api/OrderList?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`)
            .then(({ data }) => setOrders(data || []))
            .catch(console.log)
            .finally(() => setLoading(false))
    }, [startDate, endDate])

    useEffect(() => {
        fetch()
    }, [fetch])

    return <ReturnsListContext.Provider
        children={children}
        value={{
            orders,
            loading,
            dateFilter,
            setDateFilter,
            page,
            setPage,
            reload: fetch,
            hasMaxOrdersReached
        }}
    />
}
